import {
	ComponentProps,
	FocusEvent,
	type MouseEvent,
	ReactNode,
	forwardRef,
	useCallback,
	useRef,
	useState,
} from "react";
import classNames from "classnames";
import styles from "./InputCore.module.scss";

type InputCoreProps = {
	invalid?: boolean;
	readOnly?: boolean;
	disabled?: boolean;
	leadingSlot?: ReactNode;
	trailingSlot?: ReactNode;
} & ComponentProps<"div">;

// Owns border, background, radius, and slot layout only — no label, no Formik, no
// third-party imports (see input-design-system-migration-plan.md, section 3.1).
const InputCore = forwardRef<HTMLDivElement, InputCoreProps>(
	(
		{
			invalid,
			readOnly,
			disabled,
			leadingSlot,
			trailingSlot,
			className,
			children,
			onMouseDown,
			onMouseUp,
			onFocus,
			onBlur,
			...props
		},
		ref
	) => {
		// Active (mouse) vs Focus (keyboard) ring can't be done via :focus-visible alone (browsers
		// always match it on text inputs) — a mousedown immediately before focus means pointer, not keyboard.
		const pointerDownRef = useRef(false);
		const [isKeyboardFocused, setIsKeyboardFocused] = useState(false);

		const blurredAtRef = useRef<number | null>(null);
		const touchEndedAtRef = useRef<number | null>(null);
		const BLUR_RACE_WINDOW_MS = 150;
		const TOUCH_BLUR_LINK_WINDOW_MS = 50;

		const handleTouchEnd = useCallback(() => {
			touchEndedAtRef.current = performance.now();
		}, []);

		const handleMouseDown = useCallback(
			(event: MouseEvent<HTMLDivElement>) => {
				if (
					(event.nativeEvent as unknown as { __inputCoreSynthetic__?: boolean })
						.__inputCoreSynthetic__
				) {
					onMouseDown?.(event);
					return;
				}

				pointerDownRef.current = true;

				const clickedDecorativeArea =
					event.target === event.currentTarget ||
					(event.target as HTMLElement).closest("[data-input-core-slot]");
				const clickedOwnInteractiveElement = (event.target as HTMLElement).closest(
					"input, select, textarea, button, a[href]"
				);
				const justBlurred =
					blurredAtRef.current !== null &&
					performance.now() - blurredAtRef.current < BLUR_RACE_WINDOW_MS;
				blurredAtRef.current = null;

				if (clickedDecorativeArea && !clickedOwnInteractiveElement && !justBlurred) {
					const focusableChild =
						event.currentTarget.querySelector<HTMLElement>("input, select, textarea");

					if (focusableChild) {
						// preventDefault stops the browser's default blur-on-non-focusable-mousedown from undoing this.
						event.preventDefault();

						if (focusableChild instanceof HTMLSelectElement) {
							focusableChild.focus();

							// showPicker() actually opens a <select> (focus() alone can't); no-op if unsupported.
							if (typeof focusableChild.showPicker === "function") {
								try {
									focusableChild.showPicker();
								} catch {
									// unsupported/disallowed — focus() above still lets the user open it manually
								}
							}
						} else {
							// focus() alone doesn't open a custom widget like react-select's menu — only a real
							// mousedown reaching its Control does, so dispatch one instead of just focusing.
							// Targeted at the input's parent, not the input itself: react-select's own
							// Control ignores an input-targeted mousedown when closing an already-open menu.
							const syntheticMouseDown = new MouseEvent("mousedown", {
								bubbles: true,
								cancelable: true,
							});
							(
								syntheticMouseDown as unknown as {
									__inputCoreSynthetic__?: boolean;
								}
							).__inputCoreSynthetic__ = true;
							(focusableChild.parentElement ?? focusableChild).dispatchEvent(
								syntheticMouseDown
							);
							focusableChild.focus();
						}
					}
				}

				onMouseDown?.(event);
			},
			[onMouseDown]
		);

		// A mousedown that doesn't land on a focusable descendant (e.g. the wrapper's own padding,
		// or a non-interactive slot) never triggers handleFocus below, so pointerDownRef would
		// otherwise stay stuck `true` and corrupt the *next* focus event's keyboard/mouse
		// detection (e.g. a later Tab-focus would be wrongly read as a mouse focus). mouseup always
		// follows a mousedown on the same press, focus or not, so clearing it here scopes the flag
		// to "this exact press" instead of "since the last press that happened to focus something".
		const handleMouseUp = useCallback(
			(event: MouseEvent<HTMLDivElement>) => {
				pointerDownRef.current = false;
				onMouseUp?.(event);
			},
			[onMouseUp]
		);

		const handleFocus = useCallback(
			(event: FocusEvent<HTMLDivElement>) => {
				setIsKeyboardFocused(!pointerDownRef.current);
				pointerDownRef.current = false;
				onFocus?.(event);
			},
			[onFocus]
		);

		const handleBlur = useCallback(
			(event: FocusEvent<HTMLDivElement>) => {
				// Only treat this as "focus left InputCore" when it actually did — otherwise moving
				// focus between InputCore's own children (e.g. the input to its own clear button,
				// both still inside this wrapper) would flicker the keyboard-focus ring off then
				// back on for what is, from the user's perspective, a single continuous focus.
				if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
					setIsKeyboardFocused(false);

					const causedByTouch =
						touchEndedAtRef.current !== null &&
						performance.now() - touchEndedAtRef.current < TOUCH_BLUR_LINK_WINDOW_MS;
					if (causedByTouch) {
						blurredAtRef.current = performance.now();
					}
				}
				onBlur?.(event);
			},
			[onBlur]
		);

		return (
			<div
				ref={ref}
				data-invalid={invalid || undefined}
				data-readonly={readOnly || undefined}
				data-disabled={disabled || undefined}
				data-keyboard-focused={isKeyboardFocused || undefined}
				onMouseDown={handleMouseDown}
				onMouseUp={handleMouseUp}
				onTouchEnd={handleTouchEnd}
				onFocus={handleFocus}
				onBlur={handleBlur}
				className={classNames(styles["input-core"], className)}
				{...props}
			>
				{leadingSlot && (
					<span className={styles["input-core__leading-slot"]} data-input-core-slot>
						{leadingSlot}
					</span>
				)}
				{children}
				{trailingSlot && (
					<span className={styles["input-core__trailing-slot"]} data-input-core-slot>
						{trailingSlot}
					</span>
				)}
			</div>
		);
	}
);

InputCore.displayName = "InputCore";

export { InputCore };
